import { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchEntity, removeEntity } from 'cityads-v3/actions/entity'
import { ApiEntityNames } from 'cityads-v3/types/entity';
import { RootState } from 'cityads-v3/types/reducer';

export type TUseEntities<T> = [
  T | undefined,
  boolean,
  boolean,
  (params?: any) => AbortController,
  () => void,
]

/**
 *
 * @param entityName - название сущности из /actions/entity.js
 * @param aliasName - алиас для сохранения ответа в альтернативное хранилище в state.entities
 * @returns [entities, isLoading, isLoaded, load, deleteEntity]
 */
export function useEntities<T>(entityName: ApiEntityNames, aliasName?: string, autoLoad = false): TUseEntities<T> {
  const dispatch = useDispatch();
  const entities = useSelector((state: RootState) => state.entities[aliasName ?? entityName]?.data) as T;
  const isLoading = useSelector((state: RootState) => state.entities[aliasName ?? entityName]?.isFetching);
  const isLoaded = useSelector((state: RootState) => !!state.entities[aliasName ?? entityName]?.lastUpdated);

  const load = useCallback((params: any) => {
    return dispatch(
      fetchEntity({
        api: entityName,
        aliasName,
        ...params
      })
    )
  }, [dispatch, entityName])

  const remove = useCallback(() => {
    dispatch(removeEntity(aliasName ?? entityName))
  }, [dispatch, entityName])

  useEffect(() => {
    if (autoLoad && !isLoading && !isLoaded) {
      load({});
    }
  }, [autoLoad, isLoading, isLoaded, load])

  return [entities, isLoading, isLoaded, load, remove];
}

/**
 * useEntities с вызовом удаления сущности из redux при удалении компонента
 * @param entityName
 * @returns [entities, isLoading, isLoaded, load, deleteEntity]
 */
export function useEntitiesWithGC<T>(entityName: ApiEntityNames, aliasName?: string): TUseEntities<T> {
  const [entities, isLoading, isLoaded, load, remove] = useEntities<T>(entityName, aliasName);

  useEffect(() => {
    return () => {
      remove()
    }
  }, [remove])

  return [entities, isLoading, isLoaded, load, remove]
}
